import { useState } from "react";
import { toast } from "sonner";
import { Send, MapPin, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

const SUBJECTS = ["Aula experimental", "Doações e apoio", "Voluntariado", "Parcerias", "Outro"];

const EMPTY = { name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" };

const inputCls =
  "w-full bg-surface border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3.5 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada! Retornaremos em breve.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="relative py-28 sm:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4" data-testid="contact-eyebrow">CONTATO</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="contact-title">
              Faça uma visita e <span className="text-gold">treine conosco.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
              Quer inscrever seu filho, começar a treinar, ser voluntário ou apoiar o projeto?
              Envie uma mensagem — respondemos pessoalmente.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            <Reveal delay={0.1}>
              <div className="flex gap-4 items-start" data-testid="contact-address-info">
                <div className="shrink-0 w-11 h-11 rounded-full border border-crimson/50 flex items-center justify-center text-crimson">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="font-display font-semibold">R. João Ferreira da Silva, 84</p>
                  <p className="text-sm text-zinc-400">Além Ponte — Sorocaba/SP, 18013-200</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex gap-4 items-start" data-testid="contact-hours-info">
                <div className="shrink-0 w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="font-display font-semibold">Segunda e quinta-feira</p>
                  <p className="text-sm text-zinc-400 font-mono">Kids 19:00–20:30 · Adultos 20:30–22:00</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-surface border border-white/10 rounded-2xl p-7 sm:p-9 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Nome completo" className={inputCls} data-testid="contact-name-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="E-mail" className={inputCls} data-testid="contact-email-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input value={form.phone} onChange={set("phone")} placeholder="Telefone / WhatsApp (opcional)" className={inputCls} data-testid="contact-phone-input" />
              <select value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-select">
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Sua mensagem" className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-4 rounded-full hover:bg-gold-amber hover:shadow-[0_0_32px_rgba(212,175,55,0.4)] disabled:opacity-60 transition-all duration-300"
            >
              <Send size={18} /> {sending ? "Enviando..." : "Enviar Mensagem"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
